import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, ArrowUp, X, Plus, Minus, BookOpen } from 'lucide-react';
import Header from './components/Header';
import Hero from './components/Hero';
import './portfolio.css';
import './motion-theme.css';
import './story-theme.css';
import { ApproachSection, EducationSection } from './components/StorySections';
import usePortfolioMotion from './components/usePortfolioMotion';
import { projects } from './portfolioData';
import { profile } from './profileData';
import { ProfileSection, ResearchSection, ResearchDetails, JourneySection, ContactLinks } from './components/ProfileSections';
import ProjectDetails, { ProjectAwards, ProductScreenshot } from './components/ProjectDetails';
function ProjectVisual({
  kind, images
}) {
  if (kind === 'glossary') return <div className="project-visual glossary-real-visual">
    <div className="visual-caption"><span>LEARNING, MADE VISIBLE.</span><BookOpen size={20} aria-hidden="true" /></div>
    <img className="glossary-screen-main" src={images[0].src} alt={images[0].alt} width={images[0].width} height={images[0].height} loading="lazy" />
    <img className="glossary-screen-demo" src={images[1].src} alt={images[1].alt} width={images[1].width} height={images[1].height} loading="lazy" />
    <span className="visual-footnote">実際のアプリ画面</span>
  </div>;
  if (!images?.length) return null;
  const image = images[0];
  return <div className={'project-visual project-real-visual project-real-' + kind}>
    <div className="visual-caption"><span>{kind === 'lab' ? 'RESEARCH MEETS POSSIBILITY.' : kind === 'artrium' ? 'CHOSEN FOR WHO YOU ARE.' : 'WORDS, WITH INTELLIGENCE.'}</span><ArrowUpRight size={20} aria-hidden="true" /></div>
    <img className="project-real-screen" src={image.src} alt={image.alt} width={image.width} height={image.height} loading="lazy" />
    <span className="visual-footnote">{kind === 'lab' ? '実際のWebサイト画面 / 開発中' : kind === 'artrium' ? 'Studioで制作したLP' : '実際のアプリ画面'}</span>
  </div>;
}
function ContactForm() {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://embed.typeform.com/next/embed.js';
    script.async = true;
    document.body.appendChild(script);
    return () => script.remove();
  }, []);
  return <div className="contact-embed"><div data-tf-live="01J9QJT98TM82AF4W16PW61QKE" /></div>;
}
function DetailDialog({
  content,
  onClose
}) {
  const ref = useRef(null);
  useEffect(() => {
    if (!content) return;
    const dialog = ref.current;
    const oldOverflow = document.body.style.overflow;
    document.querySelectorAll('video').forEach(video => video.pause());
    document.body.style.overflow = 'hidden';
    dialog.showModal();
    return () => {
      document.body.style.overflow = oldOverflow;
      if (dialog.open) dialog.close();
    };
  }, [content]);
  if (!content) return null;
  return <dialog ref={ref} className={'detail-dialog ' + (content.id === 'contact' ? 'contact-dialog' : content.caseStudy ? 'case-study-dialog' : '')} onClose={onClose} onClick={event => {
    if (event.target === event.currentTarget) onClose();
  }} aria-labelledby="dialog-title">
    <button autoFocus className="dialog-close" onClick={onClose} aria-label="閉じる"><X size={22} /></button>
    {content.id === 'contact' ? <><p className="eyebrow">GET IN TOUCH</p><h2 id="dialog-title">Let’s connect.</h2><p className="dialog-intro">メール、またはこちらのフォームからお問い合わせください。</p><ContactLinks inDialog /><ContactForm /></> : content.id === 'research' ? <ResearchDetails /> : content.caseStudy ? <ProjectDetails project={content} /> : <><p className="eyebrow">{content.category} / {content.year}</p><h2 id="dialog-title">{content.title}</h2>{content.facts && <><p className="case-study-headline project-concept">{content.concept || content.description}</p><dl className="case-study-facts">{content.facts.map(fact => <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>)}</dl></>}{content.images?.map(image => <ProductScreenshot key={image.src} image={image} />)}<p className="dialog-intro">{content.overview}</p><div className="project-tags">{content.tags.map(tag => <span key={tag}>{tag}</span>)}</div>{content.url && <a href={content.url} target="_blank" rel="noreferrer" className="button button-dark">Visit website <ArrowUpRight size={18} /></a>}</>}
  </dialog>;
}
export default function Portfolio() {
  const rootRef = useRef(null);
  const motion = usePortfolioMotion(rootRef);
  const [detail, setDetail] = useState(null);
  const [showAll, setShowAll] = useState(false);
  return <div className="portfolio" id="home" ref={rootRef} data-motion={motion.enabled ? 'on' : 'off'}>
    <a className="skip-link" href="#work">制作物へスキップ</a>
    <Header activeSection={motion.activeSection} motionEnabled={motion.enabled} reducedMotion={motion.reduced} onToggleMotion={motion.toggleMotion} /><main><Hero motionEnabled={motion.enabled} />
      <ApproachSection />
      <section id="work" className="section work-section">
        <div className="section-heading"><div><p className="eyebrow section-index">02 / SELECTED WORK</p><h2>現場の問いを、<br /><span>動くものへ</span></h2></div><p>誰の、どんな困りごとに向き合ったか。<br />その問いから、制作の背景を紹介します。</p></div>
        <div className="work-grid">{projects.slice(0, 4).map(project => <button className={'project-card project-' + project.id} key={project.id} onClick={() => setDetail(project)} aria-label={project.title + 'の詳細を見る'}>
          <ProjectVisual kind={project.className} images={project.images} />
          <div className="project-card-copy">
            <div className="project-meta"><span>{project.category}</span><span>{project.year}</span></div>
            <div className="project-title"><h3>{project.title}</h3><span className="circle-arrow"><ArrowUpRight size={24} /></span></div>
            <p className="project-description">{project.description}</p>
            {project.status && <span className="project-development-status">{project.status}</span>}
            {project.story && <p className="project-purpose">{project.story}</p>}
            {project.highlights && <div className="project-highlights">{project.highlights.map(highlight => <span key={highlight}>{highlight}</span>)}</div>}
            {project.awards && <ProjectAwards awards={project.awards} compact />}
            <div className="project-tags">{project.tags.map(tag => <span key={tag}>{tag}</span>)}</div>
          </div>
        </button>)}</div>
        <div className="more-work"><button className="text-button" onClick={() => setShowAll(!showAll)} aria-expanded={showAll} aria-controls="more-projects">{showAll ? 'Show less' : 'More projects'}{showAll ? <Minus size={18} /> : <Plus size={18} />}</button></div>
        <div id="more-projects" className="extra-projects" hidden={!showAll}>{projects.slice(4).map(project => <button className="extra-project" key={project.id} onClick={() => setDetail(project)}><span>{project.number}</span><div><h3>{project.title}</h3><p>{project.description}</p></div><span>{project.year}</span><ArrowUpRight size={23} /></button>)}</div>
      </section>
      <ResearchSection onOpen={() => setDetail({ id: 'research' })} />
      <EducationSection />
      <JourneySection />
      <ProfileSection />
      <section id="contact" className="contact-section"><div className="contact-top"><span className="eyebrow">WHAT’S NEXT?</span><span className="contact-note">GOOD THINGS START WITH A CONVERSATION.</span></div><button className="contact-title" onClick={() => setDetail({
          id: 'contact'
        })}><span>Let’s build<br /><em>what’s next.</em></span><ArrowUpRight strokeWidth={1} /></button><div className="contact-bottom"><p>現場の課題から、次のプロジェクトへ。</p><button onClick={() => setDetail({
            id: 'contact'
          })} className="button button-dark">Get in touch <ArrowUpRight size={18} /></button></div><ContactLinks /></section>
    </main><footer className="site-footer"><a className="wordmark" href="#home">sakai<span>.</span></a><span>© {new Date().getFullYear()} {profile.englishName} · MADE WITH CURIOSITY</span><a href="#home" className="back-top">Back to top <ArrowUp size={15} /></a></footer><DetailDialog content={detail} onClose={() => setDetail(null)} />
  </div>;
}
